<template>
  <div class="mx-3">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="font-weight-bold h5 mb-0">
        {{ $t('shop.deliveryPlan.meals') }}
      </div>
      <div>
        <button
          class="btn btn-sm btn-primary mr-2"
          @click="showImport = true"
        >
          <i class="fa-solid fa-upload mr-1" />
          {{ $t('menu.import') }}
        </button>
        <button
          class="btn btn-sm btn-primary"
          @click="openCreateModal"
        >
          <i class="fas fa-plus mr-1" />
          {{ $t('general.add') }}
        </button>
      </div>
    </div>

    <template v-if="showImport">
      <MealsImport
        :catalog-id="catalogId"
        @close="showImport = false; loadMeals()"
      />
    </template>
    <template v-else>
      <!-- Search Bar -->
      <div class="mb-3">
        <div class="input-group">
          <input
            v-model="searchQuery"
            type="text"
            class="form-control"
            :placeholder="`${$t('general.search')}...`"
          >
        </div>
      </div>

      <div v-if="pending">
        <Loader />
      </div>

      <div
        v-else
        style="height: calc(100vh - 300px); overflow: auto; padding-bottom: 100px"
      >
        <!-- Ingredients List -->
        <div
          v-if="filteredMeals.length === 0"
          class="text-muted text-center py-4"
        >
          <i class="far fa-pot-food fa-2x mb-2" />
          <div v-if="searchQuery">
            {{ $t('shop.deliveryPlan.noMealsFound', { searchQuery }) }}
          </div>
          <div v-else>
            {{ $t('shop.deliveryPlan.noMealsAvailable') }}
          </div>
        </div>

        <div
          v-else
          class="list-group"
        >
          <div
            v-for="meal in filteredMeals"
            :key="meal.id"
            class="list-group-item d-flex justify-content-between align-items-center"
          >
            <div>
              <div class="font-weight-bold">
                <i class="far fa-pot-food mr-1" />
                {{ meal.name }}
              </div>
            </div>
            <div class="btn-group">
              <button
                class="btn btn-sm btn-outline-primary"
                title="Edit meal"
                @click="openEditModal(meal)"
              >
                <i class="fas fa-edit" />
              </button>
              <button
                class="btn btn-sm btn-outline-danger"
                title="Delete meal"
                @click="deleteMeal(meal)"
              >
                <i class="fas fa-trash" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <BModal
        v-model="showModal"
        hide-header
        hide-footer
        size="lg"
        :title="editingMeal ? $t('shop.deliveryPlan.editMeal') : $t('shop.deliveryPlan.addMeal')"
        :ok-title="editingMeal ? $t('general.save') : $t('general.add')"
        :cancel-title="$t('general.cancel')"
      >
        <div class="mb-3">
          <h5 class="modal-title font-weight-bold">
            <i class="far fa-pot-food mr-2" />
            {{ editingMeal ? $t('shop.deliveryPlan.editMeal') : $t('shop.deliveryPlan.addMeal') }}
          </h5>
          <button
            type="button"
            class="close"
            @click="closeModal"
          >
            <span>&times;</span>
          </button>
        </div>
        <div>
          <MealForm
            ref="mealForm"
            :key="editingMeal?.id"
            :meal="editingMeal"
            :ingredients="ingredients"
            :meal-sizes="mealSizes"
            @saved="saved"
          />
        </div>
        <hr>
        <div class="d-flex justify-content-end">
          <button
            class="btn btn-secondary"
            @click="closeModal"
          >
            {{ $t('general.cancel') }}
          </button>
          <button
            class="btn btn-primary ml-3"
            @click="$refs.mealForm.saveMeal()"
          >
            {{ $t('general.save') }}
          </button>
        </div>
      </BModal>
    </template>
  </div>
</template>

<script>
import axios from '@/api/axios';
import MealForm from './MealForm';
import MealsImport from './MealsImport';

export default {
  props: {
    catalogId: String,
  },
  data: () => ({
    meals: [],
    ingredients: [],
    mealSizes: [],
    pending: false,
    searchQuery: '',
    showModal: false,
    editingMeal: null,
    errors: {},
    showImport: false,
  }),
  components: {
    MealForm,
    MealsImport,
  },
  computed: {
    filteredMeals() {
      if (!this.searchQuery.trim()) {
        return this.meals;
      }
      const query = this.searchQuery.toLowerCase();
      return this.meals.filter((meal) =>
        meal.name.toLowerCase().includes(query));
    },
  },
  methods: {
    saved() {
      this.loadMeals();
      this.closeModal();
    },
    loadMeals() {
      if (this.meals.length === 0) {
        this.pending = true;
      }
      axios.get(`shop/${this.catalogId}/meals`)
        .then(({ data }) => {
          this.meals = data.meals;
        })
        .catch(() => {
          this.$toast.error('Failed to load meals');
        })
        .finally(() => {
          this.pending = false;
        });
    },

    openCreateModal() {
      this.editingMeal = null;
      this.resetForm();
      this.showModal = true;
    },

    openEditModal(meal) {
      this.editingMeal = meal;
      this.errors = {};
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.editingMeal = null;
      this.resetForm();
    },

    resetForm() {
      this.form = {
        title: '',
        description: '',
      };
      this.errors = {};
    },

    validateForm() {
      this.errors = {};

      if (!this.form.title.trim()) {
        this.errors.title = 'Title is required';
      }

      return Object.keys(this.errors).length === 0;
    },

    async deleteMeal(meal) {
      try {
        await axios.delete(`shop/${this.catalogId}/meals/${meal.id}`);
        this.loadMeals();
      } catch (error) {
        this.$toast.error('Failed to delete meal');
      }
    },
  },
  created() {
    this.loadMeals();

    axios.get(`shop/${this.catalogId}/meals/ingredients`)
      .then(({ data }) => {
        this.ingredients = data.ingredients;
        this.mealSizes = data.mealSizes;
      });
  },
};
</script>

<style scoped>
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-dialog {
  max-width: 1000px;
}

.close {
  font-size: 2rem;
  position: absolute;
  right: 2rem;
  top: 1rem;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.btn-group .btn {
  margin-left: 0.25rem;
}

.btn-group .btn:first-child {
  margin-left: 0;
}
</style>
